import {
  Link,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ProgressBar from '@/components/shared/ProgressBar';
import AgentsSelection from '@/features/appointments/components/AgentsSelection';
import useAgentsAndTimeslots from '@/features/scrollFlow/pages/Calendar/hooks/useAgentsAndTimeslots';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import LocationSearchBox from './LocationSearchBox';

const SelectAgent: React.FC = () => {
  const tagging = useTagging();
  const [tagged, setTagged] = useState(false);

  const selectedAppointmentType = useAppointmentStore(
    (state) => state.selectedAppointmentType,
  );
  const { agentsForSelectedTimeSlot, isLoading: isLoadingAgents } =
    useAgentsAndTimeslots();

  const selectedAgent = useAppointmentStore((state) => state.selectedAgent);
  const setSelectedAgent = useAppointmentStore(
    (state) => state.setSelectedAgent,
  );
  const setSelectedBranch = useAppointmentStore(
    (state) => state.setSelectedBranch,
  );
  const { selectedDestination, contactInfo, searchedDestination, userSearch } =
    useAppointmentStore();
  const { sessionId, sessionSource } = useSessionStore();

  useLandingPageRedirection();
  useProgress(60);

  const navigate = useNavigate();
  const { id } = useParams();

  const isInBranchAppointment = selectedAppointmentType === 'in-branch';
  const isOnlineAppointment = selectedAppointmentType === 'online';
  const isButtonDisabled = !selectedAgent?.agentRecordId;

  useEffect(() => {
    if (!tagged) {
      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;
      tagging.tag('view', sessionSource, {
        page: Page.selectAgent,
        sessionid: sessionId,
        flow: '',
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });

      setTagged(true);
    }
  }, [
    tagged,
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
    searchedDestination,
    userSearch,
  ]);

  useEffect(() => {
    if (isInBranchAppointment && selectedAgent?.location) {
      setSelectedBranch({
        branchId: selectedAgent.serviceTerritoryId ?? '',
        branchName: selectedAgent.location,
        branchAddress: '', // TODO: Add branch address from agent
        distance: 0,
      });
    }
  }, [selectedAgent, isInBranchAppointment, setSelectedBranch]);

  const handleClickContinue = () => {
    if (!selectedAgent?.agentRecordId) {
      return;
    }

    navigate(`/schedule-appointment/${id}/contact-info`);
  };

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 2,
          pt: 4,
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            maxWidth: '600px',
            textAlign: 'left',
            minHeight: 0,
          }}
        >
          <Typography variant='h5'>
            {isInBranchAppointment
              ? 'Type in your preferred city or ZIP code.'
              : 'Please select an agent below.'}
          </Typography>

          {isInBranchAppointment && <LocationSearchBox />}

          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {agentsForSelectedTimeSlot.length === 0 && !isLoadingAgents ? (
              <Typography variant='body1'>
                No nearby agents are available for the selected time slot.
                Please try{' '}
                <Link onClick={() => navigate(-1)}>
                  selecting a different time slot
                </Link>{' '}
                or adjust your location search criteria.
              </Typography>
            ) : (
              <AgentsSelection
                agents={agentsForSelectedTimeSlot}
                selectedAgent={selectedAgent}
                onSelectAgent={setSelectedAgent}
                isLoading={isLoadingAgents}
              />
            )}
          </Stack>
        </Stack>
      </Stack>

      <ProgressBar
        buttonDisabled={isButtonDisabled}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

export default SelectAgent;
